/**
 * THE CLAIMS CHECK ON WORDS A PERSON TYPED FOR A CLIENT (ops #32; ops #46 began it for the pay page).
 * The functions only: the rules are data in `claims-rules.ts`, the one file allowed to spell what they
 * forbid, so nothing here names a forbidden word and the house-style scan reads this file like any
 * other. The Worker refuses with `checkWords` (`api/src/lib/claims.ts`, inside every composer of a
 * message a person typed) and the screen warns with `warningsIn` and `looksLikeACode` as the office
 * types. The rule table of 2026-10-16 governs both: the server refuses only what it can recognise with
 * high precision, and a generic word warns on the screen instead.
 *
 * HOW A TEXT IS READ. Taken to NFKC, as the home words test takes it, so a fullwidth letter is a
 * letter. Then the one subtraction made before any rule runs: every day in the shapes the office
 * types, `11/5`, `4/5` (of the rooms), `5/5/26`, `10/5/2026` (`TYPED_DATES`), because the copied rating
 * rule reads a slash and a five as a score out of five. A slash pair after "rated", "rating", "score"
 * or "stars", or before "stars", or after a decimal point, is a rating and is left in. Nothing else is
 * subtracted and no sentence is allowlisted.
 *
 * WHAT RUNS. The business's copy fires as the hook fires, less the two rules about the medium
 * (`MEDIUM_RULES`) and less a match `COPIED_RULE_EXCEPTIONS` spares, where a shape names somebody
 * else; ops's own rules beside it, each anchor matched on its own so a finding names every shape it
 * stands on. A finding inside a longer finding of the same rule is dropped, and each phrase is named
 * once, in the order typed.
 *
 * THE TWO RULES ABOUT THE MEDIUM ARE SKIPPED. The hook catches a sentence that tells somebody to send
 * a review request or an offer by text, and in a text to a client the medium is given: "thanks for
 * your text, I will review the photos" would trip it. What matters is the ask itself, and ops's own
 * `review_ask` and `cut` refuse it; `api/test/claims.test.ts` proves the cover. The copy keeps every
 * row of the hook, so a re-sync stays byte for byte.
 *
 * AN ALLOWED AMOUNT PASSES BY VALUE. Every figure `amount` finds is read to cents (`figureCents`) and
 * dropped only when its value is one the caller allows, an invoice's own total or balance, or her
 * price of one clean on a change to her standing cleans (section 12.4 of the home contract). Nothing is
 * cut out of the words, so `$2125.00` on a $212.00 balance is refused whole. A figure is money only
 * with a currency mark or a money word, on an invoice text as anywhere: a year, an address, a count or
 * the card's last four never reaches the rule. An unmarked figure beside a price is the screen's amber.
 */
import {
  BUSINESS_RULES,
  COPIED_RULE_EXCEPTIONS,
  FIGURE_BESIDE_A_MARK,
  MONTH_NAMES,
  OPS_RULES,
  OPS_WARNINGS,
  STREET_SUFFIXES,
  type ClaimsCode,
  type ClaimsRule,
} from './claims-rules.ts';
import { BUSINESS_PHONE_DISPLAY, BUSINESS_PHONE_E164 } from './enums.ts';

/** One thing found in the words: which code refuses it, which rule, the words as typed, what to do. */
export interface ClaimsFinding {
  readonly code: ClaimsCode;
  readonly rule: string;
  readonly phrase: string;
  readonly say: string;
}

/** One amber warning on the screen, which never refuses. */
export interface ClaimsWarning {
  readonly rule: string;
  readonly phrase: string;
  readonly say: string;
}

/**
 * What a caller lets a message name, in cents: an invoice message, its own total and balance; a change
 * to her standing cleans, her price of one clean under it.
 */
export interface AllowedAmounts {
  readonly amountsCents?: readonly number[];
}

/** The hook's two rules about the medium, copied and pinned, and never run on a text to a client. */
export const MEDIUM_RULES = ['sms-review-request', 'sms-marketing'] as const;

/**
 * Every day in the shapes the office types it, `11/5`, `5/5/26`, `10/5/2026`: the one subtraction
 * made before any rule runs. Not a figure after a decimal point (`4.5/5`), not one after "rated",
 * "rating", "score" or "stars", and not one followed by "stars", which are ratings.
 */
export const TYPED_DATES =
  /(?<![\d.])(?<!\b(?:rated|ratings?|rating\s+of|scored?|score\s+of|stars?)\s+)\b\d{1,2}\/\d{1,2}(?:\/\d{2,4})?\b(?!\s*stars?\b)/gi;

/** The words that name something a code opens, each matched whole (the home words test, ops #19). */
export const CODE_WORDS: readonly string[] = [
  'code',
  'pin',
  'passcode',
  'password',
  'combination',
  'combo',
  'lockbox',
  'keypad',
  'alarm',
  'gate',
  'garage',
  'door',
  'key',
  'safe',
];

/** The amber sentence beside words that read like a code. */
export const CODE_WARNING =
  'This reads as if it carries a door, gate, lockbox or alarm code. A text is not where a code goes.';

/** The business's own number, in the four forms ops writes it, the longest first. */
const BUSINESS_NUMBER_FORMS: readonly string[] = [
  BUSINESS_PHONE_DISPLAY,
  BUSINESS_PHONE_E164,
  BUSINESS_PHONE_E164.slice(1),
  BUSINESS_PHONE_E164.slice(2),
];

/** A day of the month as typed after its month: `5`, `5th`. */
const DAY = '\\d{1,2}(?:st|nd|rd|th)?';

/**
 * A day with its month spelled, as ops writes it and the office types it: `September 17, 2026`, a
 * bare year after a month (`October 2026`), and a list of days after one month (`October 5 and 19`,
 * `December 24 and 25`, `October 5, 12 and 19`).
 */
const MONTH_DAY = new RegExp(
  `\\b(?:${MONTH_NAMES})\\.?\\s+(?:${DAY}(?:(?:\\s*(?:,|-|\\u2013)\\s*|\\s+)(?:(?:and|&|or|to)\\s+)?${DAY}\\b)*(?:,?\\s+\\d{4})?|\\d{4})\\b`,
  'gi',
);

/** A time of day, `9:00` or `10:30`. */
const CLOCK_TIME = /\b\d{1,2}:\d{2}\b/g;

/** An address she taps or writes to, whose digits are never a figure. */
const LINK_OR_MAIL = /\bhttps?:\/\/\S+|\bwww\.\S+|\S+@\S+\.\S+/gi;

/** A street address: a figure, then within three words a street suffix (`412 Bayshore Dr`). */
const STREET_ADDRESS = new RegExp(
  `\\b\\d+[a-z]?\\s+(?:[a-z]+\\s+){0,2}(?:${STREET_SUFFIXES})\\b\\.?`,
  'gi',
);

/** A figure after a label that makes it a number of something, never an amount: `Invoice: 1042`. */
const LABELLED_FIGURE =
  /(\b(?:ending|invoice|inv|number|receipt|order|ref|formula|route|exit|unit|apt|suite|ste|building|bldg|box|zip)\.?:?\s*#?\s*|#\s*|\bno\.\s*|\b(?:i|us)-)\d[\d,]*/gi;

/** A currency mark anywhere in the words: the mark of an invoice text. */
const CURRENCY_MARK = /[$€£]/;

/** The warning a figure beside a mark is drawn under. */
const FIGURE_WARNING = 'figure_without_a_mark';

/** Every match of a pattern replaced by spaces of its length, so every offset stays where it was. */
const blank = (text: string, pattern: RegExp): string =>
  text.replace(pattern, (match) => ' '.repeat(match.length));

/** The text as every refusal reads it: NFKC, with the days the office types blanked. */
function prepared(text: string): string {
  return blank(text.normalize('NFKC'), TYPED_DATES);
}

/**
 * As the warnings read it: also without the business number, a link or a mail address, a spelled
 * day, a time of day, a street address, or a figure a label makes a number of something.
 */
function preparedForWarnings(text: string): string {
  let words = prepared(text);
  for (const form of BUSINESS_NUMBER_FORMS) {
    words = words.split(form).join(' '.repeat(form.length));
  }
  words = blank(words, LINK_OR_MAIL);
  words = blank(words, MONTH_DAY);
  words = blank(words, CLOCK_TIME);
  words = blank(words, STREET_ADDRESS);
  return words.replace(
    LABELLED_FIGURE,
    (match, label: string) => label + ' '.repeat(match.length - label.length),
  );
}

const withG = (flags: string) => (flags.includes('g') ? flags : `${flags}g`);

/** A match a copied rule makes and an exception spares: its words, what comes before and after. */
interface Exception {
  readonly match?: RegExp;
  readonly before?: RegExp;
  readonly after?: RegExp;
}

/** One rule ready to run: every pattern it matches with, the code it refuses under, its exceptions. */
interface Matcher {
  readonly rule: ClaimsRule;
  readonly code: ClaimsCode;
  readonly patterns: readonly RegExp[];
  readonly exceptions: readonly Exception[];
}

const SKIPPED: ReadonlySet<string> = new Set(MEDIUM_RULES);

const exceptionsOf = (id: string): Exception[] =>
  (COPIED_RULE_EXCEPTIONS[id] ?? []).map(({ match, before, after }) => ({
    ...(match === undefined ? {} : { match: new RegExp(`^(?:${match})$`, 'i') }),
    ...(before === undefined ? {} : { before: new RegExp(before, 'i') }),
    ...(after === undefined ? {} : { after: new RegExp(after, 'i') }),
  }));

/** The rules that refuse a claim: the business's copy, less the medium, then ops's claim rules. */
const CLAIM_RULES: readonly Matcher[] = [
  ...BUSINESS_RULES.filter((rule) => !SKIPPED.has(rule.id)).map((rule) => ({
    rule,
    code: 'claim_not_allowed' as ClaimsCode,
    patterns: [new RegExp(rule.source, withG(rule.flags))],
    exceptions: exceptionsOf(rule.id),
  })),
  ...OPS_RULES.filter((rule) => rule.code === 'claim_not_allowed').map((rule) => ({
    rule,
    code: rule.code,
    patterns: rule.anchors.map((anchor) => new RegExp(anchor, withG(rule.flags))),
    exceptions: [],
  })),
];

/** The rules that refuse a price, `amount` first among them. */
const PRICE_RULES: readonly Matcher[] = OPS_RULES.filter(
  (rule) => rule.code === 'price_not_allowed',
).map((rule) => ({
  rule,
  code: rule.code,
  patterns: rule.anchors.map((anchor) => new RegExp(anchor, withG(rule.flags))),
  exceptions: [],
}));

/** The price rules with no figure in them: a cut, a credit, "free". A page shows none of them. */
const CUT_RULES = PRICE_RULES.filter(({ rule }) => rule.id !== 'amount');

/** The screen's amber, each anchor on its own. */
const WARNING_RULES = OPS_WARNINGS.map((rule) => ({
  rule,
  patterns: rule.anchors.map((anchor) => new RegExp(anchor, withG(rule.flags))),
}));

const FIGURE_BESIDE_A_MARK_PATTERN = new RegExp(FIGURE_BESIDE_A_MARK, 'gi');

interface Found {
  readonly at: number;
  readonly end: number;
  readonly order: number;
  readonly finding: ClaimsFinding;
}

/** Whether an exception spares the words `at` to `end`. */
const spared = (words: string, at: number, end: number, exception: Exception): boolean =>
  (exception.match === undefined || exception.match.test(words.slice(at, end))) &&
  (exception.before === undefined || exception.before.test(words.slice(0, at))) &&
  (exception.after === undefined || exception.after.test(words.slice(end)));

function foundBy(words: string, matchers: readonly Matcher[], first: number): Found[] {
  const found: Found[] = [];
  matchers.forEach(({ rule, code, patterns, exceptions }, index) => {
    for (const pattern of patterns) {
      for (const match of words.matchAll(pattern)) {
        if (match[0] === '') continue;
        const at = match.index;
        const end = at + match[0].length;
        if (exceptions.some((exception) => spared(words, at, end, exception))) continue;
        found.push({
          at,
          end,
          order: first + index,
          finding: { code, rule: rule.id, phrase: match[0].trim(), say: rule.say },
        });
      }
    }
  });
  return found;
}

/**
 * In the order typed, the earlier rule first at one place, each phrase once whatever its casing, and
 * never a finding a longer finding of the same rule already holds ("212.00" inside "comes to 212.00").
 */
function inOrder(found: readonly Found[]): ClaimsFinding[] {
  const held = (one: Found) =>
    found.some(
      (other) =>
        other !== one &&
        other.finding.rule === one.finding.rule &&
        other.at <= one.at &&
        one.end <= other.end &&
        other.end - other.at > one.end - one.at,
    );
  const seen = new Set<string>();
  return found
    .filter((one) => !held(one))
    .sort((a, b) => a.at - b.at || a.order - b.order)
    .filter(({ finding }) => {
      const key = finding.phrase.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(({ finding }) => finding);
}

const FIGURE = /\d[\d,]*(?:\.\d+)?/;

/**
 * THE FIGURE IN A PHRASE `amount` FOUND, in cents: the dollar sign, commas, spaces and any word around
 * it left out. `212` is 21200, `1,212.00` is 121200, and `50 cents` is 50. Null for a figure it cannot
 * be sure of, a fraction of a dollar not in two places, which is then refused as typed.
 */
export function figureCents(phrase: string): number | null {
  const figure = FIGURE.exec(phrase)?.[0];
  if (figure === undefined) return null;
  const [whole = '', cents] = figure.replace(/,/g, '').split('.');
  if (!/^\d+$/.test(whole)) return null;
  if (cents !== undefined && !/^\d{2}$/.test(cents)) return null;
  if (cents === undefined && /\bcents?\b/i.test(phrase)) {
    const value = Number(whole);
    return Number.isSafeInteger(value) ? value : null;
  }
  const value = Number(whole) * 100 + (cents === undefined ? 0 : Number(cents));
  return Number.isSafeInteger(value) ? value : null;
}

function claimsFound(words: string): Found[] {
  return foundBy(words, CLAIM_RULES, 0);
}

function pricesFound(words: string, allowed: AllowedAmounts): Found[] {
  const amounts = new Set(allowed.amountsCents ?? []);
  return foundBy(words, PRICE_RULES, CLAIM_RULES.length).filter(({ finding }) => {
    if (finding.rule !== 'amount') return true;
    const cents = figureCents(finding.phrase);
    return cents === null || !amounts.has(cents);
  });
}

/**
 * Every claim and every cut in the words a client may not be told, as written there, in order: the
 * claims, and the price rules with no figure in them (a cut, a credit, "free"), which a page holds to
 * as firmly as a text. A figure is `priceIn`'s, since a page shows her balance. Empty is clean.
 */
export function claimsIn(text: string): string[] {
  return checkClaimsAndCuts(text).map((finding) => finding.phrase);
}

/**
 * `claimsIn`'s findings whole, for a refusal: every claim and every cut, as `checkWords` finds them,
 * and never a figure. For words where a figure is what a thing at the house was worth and a person at
 * the office reads them before home is sent them: what went wrong on a visit (ops #68).
 */
export function checkClaimsAndCuts(text: string): ClaimsFinding[] {
  const words = prepared(text);
  return inOrder([...claimsFound(words), ...foundBy(words, CUT_RULES, CLAIM_RULES.length)]);
}

/** Every price in the words, as written there, in order, less an amount allowed by its value. */
export function priceIn(text: string, allowed: AllowedAmounts = {}): string[] {
  return inOrder(pricesFound(prepared(text), allowed)).map((finding) => finding.phrase);
}

/** Every claim and every price in the words, each phrase once, in the order typed. Empty is clean. */
export function checkWords(text: string, allowed: AllowedAmounts = {}): ClaimsFinding[] {
  const words = prepared(text);
  return inOrder([...claimsFound(words), ...pricesFound(words, allowed)]);
}

/**
 * THE SCREEN'S AMBER: the six rows of `OPS_WARNINGS`, and on a text that holds a currency mark every
 * unmarked figure of 13 or more the blanking left standing, each where no refusal already stands on
 * the same words. Never a refusal, and never read by the server.
 */
export function warningsIn(text: string): ClaimsWarning[] {
  const words = preparedForWarnings(text);
  const refused = [...claimsFound(words), ...pricesFound(words, {})];
  const overlaps = (at: number, end: number) =>
    refused.some((found) => found.at < end && at < found.end);
  const marked = CURRENCY_MARK.test(words);
  const warnings: ClaimsWarning[] = [];
  const seen = new Set<string>();
  for (const { rule, patterns } of WARNING_RULES) {
    const all =
      marked && rule.id === FIGURE_WARNING ? [...patterns, FIGURE_BESIDE_A_MARK_PATTERN] : patterns;
    for (const pattern of all) {
      for (const match of words.matchAll(pattern)) {
        const phrase = match[0].trim();
        if (phrase === '' || overlaps(match.index, match.index + match[0].length)) continue;
        if (seen.has(`${rule.id}:${phrase.toLowerCase()}`)) continue;
        seen.add(`${rule.id}:${phrase.toLowerCase()}`);
        warnings.push({ rule: rule.id, phrase, say: rule.say });
      }
    }
  }
  return warnings;
}

/** Rule (b) of the home words test: a code word, then a digit, a hash or a star within forty. */
const CODE_WORD_THEN_DIGIT = new RegExp(`\\b(?:${CODE_WORDS.join('|')})\\b[\\s\\S]{0,39}[\\d#*]`);

/**
 * WHETHER THE WORDS READ LIKE A DOOR, GATE, LOCKBOX OR ALARM CODE, for the screen's amber: rule (b)
 * of the words test alone, once the business's number is out and a run of spaces, hyphens and full
 * stops between two digits is closed up. Rule (a), four digits together, is not used: it fires on
 * `10:30` once the colon goes, and a time of day is not a code.
 */
export function looksLikeACode(text: string): boolean {
  let words = text.normalize('NFKC');
  for (const form of BUSINESS_NUMBER_FORMS) words = words.split(form).join('');
  words = words.toLowerCase().replace(/(?<=\d)[ .-]+(?=\d)/g, '');
  return CODE_WORD_THEN_DIGIT.test(words);
}

const WHAT_IT_IS: Readonly<Record<ClaimsCode, string>> = {
  claim_not_allowed: 'is a claim the business cannot make',
  price_not_allowed: 'is a price, and prices are never typed here',
};

/** The code a refusal answers with: a claim before a price, since a claim is the graver. */
export function claimsCodeOf(findings: readonly ClaimsFinding[]): ClaimsCode | null {
  if (findings.length === 0) return null;
  return findings.some((finding) => finding.code === 'claim_not_allowed')
    ? 'claim_not_allowed'
    : 'price_not_allowed';
}

/** One finding as a sentence: the phrase, what it is, and what to do about it. */
const findingSentence = (finding: ClaimsFinding): string =>
  `"${finding.phrase}" ${WHAT_IT_IS[finding.code]}. ${finding.say}`;

/** THE REFUSAL IN THE OFFICE'S WORDS, built here once for the server and the screen. */
export function claimsRefusalMessage(findings: readonly ClaimsFinding[]): string {
  return ['Not sent.', ...findings.map(findingSentence), 'Change the words and send again.'].join(
    ' ',
  );
}

/**
 * ONE FIELD OF A RECORD REFUSED (ops #68): the request's own name for it, so a screen puts the refusal
 * under the box it came from, the field's label as the screen shows it, and what the check found in
 * its words.
 */
export interface ClaimsFieldFindings {
  readonly field: string;
  readonly label: string;
  readonly findings: readonly ClaimsFinding[];
}

/**
 * THE REFUSAL OF WORDS TYPED ONTO A RECORD HOME CARRIES TO HER PAGE (ops #68), in the same sentences
 * as a message's, each field named by its label: what was not done first ("Not submitted."), then
 * every field and what was found in it, then what to do again ("submit it again").
 */
export function fieldsRefusalMessage(
  notDone: string,
  fields: readonly ClaimsFieldFindings[],
  again: string,
): string {
  return [
    notDone,
    ...fields.flatMap((field) => [`In "${field.label}":`, ...field.findings.map(findingSentence)]),
    `Change the words there and ${again}.`,
  ].join(' ');
}
